import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Database, Search, Table2, LayoutGrid, ChevronLeft, ChevronRight, FileJson, Boxes } from "lucide-react";

const toCell = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// Read-only dataset browser shared by admin (light) and portal (dark).
export const DataBrowser = ({ listPath, recordsPath, dark = false, renderActions, reloadKey }) => {
  const [datasets, setDatasets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState({ records: [], total: 0, page: 1, page_size: 25 });
  const [search, setSearch] = useState("");
  const [view, setView] = useState("table");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = dark
    ? { panel: "bg-white/[0.035] border-[#67F2B3]/12", text: "text-[#F4FFF9]", sub: "text-[#F4FFF9]/50",
        border: "border-white/10", sel: "bg-[#67F2B3] text-[#071A13]", hov: "hover:bg-white/5",
        input: "bg-[#071A13] border-white/10 text-[#F4FFF9]", accent: "text-[#67F2B3]", chip: "bg-white/5 border-white/10" }
    : { panel: "bg-white border-[#E2EBF0]", text: "text-[#0D3B2D]", sub: "text-[#4A635A]",
        border: "border-[#E2EBF0]", sel: "bg-[#0D3B2D] text-white", hov: "hover:bg-[#F4FFF9]",
        input: "bg-white border-[#E2EBF0] text-[#0D3B2D]", accent: "text-[#10B981]", chip: "bg-[#F4FFF9] border-[#E2EBF0]" };

  const loadDatasets = useCallback(() => {
    api.get(listPath).then((r) => {
      setDatasets(r.data);
      setSelected((cur) => cur || (r.data[0]?.dataset_id ?? null));
    }).catch(() => {});
  }, [listPath]);

  useEffect(() => { loadDatasets(); }, [loadDatasets, reloadKey]);

  const loadRecords = useCallback((dsId, page = 1, q = "") => {
    if (!dsId) { setData({ records: [], total: 0, page: 1, page_size: 25 }); return; }
    setLoading(true);
    api.get(`${recordsPath(dsId)}?page=${page}&page_size=25&search=${encodeURIComponent(q)}`)
      .then((r) => setData(r.data)).catch(() => setData({ records: [], total: 0, page: 1, page_size: 25 }))
      .finally(() => setLoading(false));
  }, [recordsPath]);

  useEffect(() => { if (selected) loadRecords(selected, 1, ""); setSearch(""); }, [selected, loadRecords]);

  const current = datasets.find((d) => d.dataset_id === selected);
  const fields = current?.fields || [];
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div className="grid md:grid-cols-[230px_1fr] gap-4" data-testid="data-browser">
      {/* Collections sidebar */}
      <div className={`border rounded-xl p-3 ${t.panel} h-max`} data-testid="dataset-sidebar">
        <div className={`flex items-center gap-2 text-xs uppercase tracking-wide mb-3 ${t.sub}`}>
          <Boxes className="h-3.5 w-3.5" /> Collections
        </div>
        {datasets.length === 0 ? (
          <p className={`text-sm ${t.sub}`}>No data available yet.</p>
        ) : (
          <ul className="space-y-1">
            {datasets.map((d) => (
              <li key={d.dataset_id}>
                <button onClick={() => setSelected(d.dataset_id)} data-testid={`dataset-item-${d.dataset_id}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-between gap-2 ${selected === d.dataset_id ? t.sel : `${t.text} ${t.hov}`}`}>
                  <span className="truncate flex items-center gap-2"><Database className="h-3.5 w-3.5 shrink-0" />{d.name}</span>
                  <span className="text-[10px] font-mono opacity-70 shrink-0">{d.record_count}</span>
                </button>
                {renderActions && selected === d.dataset_id && (
                  <div className="px-2 pt-1 pb-1">{renderActions(d, loadDatasets)}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Records */}
      <div className={`border rounded-xl p-4 ${t.panel} min-w-0`}>
        {!current ? (
          <div className={`py-16 text-center text-sm ${t.sub}`}>Select a collection to browse its records.</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div className="min-w-0">
                <div className={`font-heading font-semibold ${t.text} flex items-center gap-2`}>
                  <FileJson className={`h-4 w-4 ${t.accent}`} /> {current.name}
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${t.chip} ${t.sub}`}>{current.source_type}</span>
                </div>
                {current.description && <p className={`text-xs mt-0.5 ${t.sub}`}>{current.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setView("table")} data-testid="data-view-table"
                  className={`p-1.5 rounded ${view === "table" ? t.accent : t.sub}`}><Table2 className="h-4 w-4" /></button>
                <button onClick={() => setView("card")} data-testid="data-view-card"
                  className={`p-1.5 rounded ${view === "card" ? t.accent : t.sub}`}><LayoutGrid className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <div className={`flex items-center gap-2 flex-1 border rounded-lg px-3 py-1.5 ${t.input}`}>
                <Search className={`h-4 w-4 ${t.sub}`} />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadRecords(selected, 1, search); }}
                  placeholder="Search records…" data-testid="data-search-input"
                  className="flex-1 bg-transparent outline-none text-sm" />
              </div>
              <button onClick={() => loadRecords(selected, 1, search)} data-testid="data-search-btn"
                className={`text-sm px-3 py-1.5 rounded-lg ${t.sel}`}>Search</button>
            </div>

            <div className={`text-[11px] ${t.sub} mb-2`} data-testid="data-record-count">
              {loading ? "Loading…" : `${data.total} record${data.total === 1 ? "" : "s"}`}
            </div>

            {data.records.length === 0 ? (
              <div className={`py-12 text-center text-sm ${t.sub}`}>No records match.</div>
            ) : view === "table" ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="data-table">
                  <thead>
                    <tr className={`border-b ${t.border}`}>
                      {fields.map((f) => <th key={f} className={`text-left font-medium py-2 pr-4 whitespace-nowrap ${t.sub}`}>{f}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.records.map((rec, i) => (
                      <tr key={i} onClick={() => setDetail(rec.data)} data-testid={`data-row-${i}`}
                        className={`border-b ${t.border} cursor-pointer ${t.hov}`}>
                        {fields.map((f) => (
                          <td key={f} className={`py-2 pr-4 max-w-[240px] truncate ${t.text}`}>{toCell(rec.data?.[f])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {data.records.map((rec, i) => (
                  <button key={i} onClick={() => setDetail(rec.data)} data-testid={`data-card-${i}`}
                    className={`text-left border rounded-lg p-3 ${t.border} ${t.hov}`}>
                    {fields.slice(0, 6).map((f) => (
                      <div key={f} className="flex gap-2 text-xs py-0.5">
                        <span className={`${t.sub} shrink-0`}>{f}:</span>
                        <span className={`${t.text} truncate`}>{toCell(rec.data?.[f])}</span>
                      </div>
                    ))}
                  </button>
                ))}
              </div>
            )}

            {data.total > data.page_size && (
              <div className="flex items-center justify-between mt-4">
                <span className={`text-xs ${t.sub}`}>Page {data.page} of {totalPages}</span>
                <div className="flex gap-2">
                  <button disabled={data.page <= 1} onClick={() => loadRecords(selected, data.page - 1, search)}
                    data-testid="data-prev-page" className={`p-1.5 rounded-lg border ${t.border} ${t.text} disabled:opacity-30`}><ChevronLeft className="h-4 w-4" /></button>
                  <button disabled={data.page >= totalPages} onClick={() => loadRecords(selected, data.page + 1, search)}
                    data-testid="data-next-page" className={`p-1.5 rounded-lg border ${t.border} ${t.text} disabled:opacity-30`}><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="data-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading text-[#0D3B2D]">Record detail</DialogTitle></DialogHeader>
          <pre className="text-xs bg-[#071A13] text-[#67F2B3] rounded-lg p-4 overflow-x-auto font-mono whitespace-pre-wrap break-words">
            {detail ? JSON.stringify(detail, null, 2) : ""}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};
